import { useApolloClient, useFragment, useLazyQuery, useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useState } from "react";
import { useStore } from "react-redux";
import type { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { resolveEventViewWarm } from "@ppb/tbd-store/state/entities/sport-events/event-view-warm-selectors";
import { gql } from "../../../types/__generated__";
import type { EventFixtureViewFragment as EventFixtureViewFragmentData } from "../../../types/__generated__/graphql";
import {
  EventViewLabelWarmFragment,
  readEventViewWarmState,
  writeEventViewBaseFixtureWarm,
  writeEventViewFixtureWarm,
  writeEventViewLabelWarm,
} from "../../../apollo-client/fragments/event-view.graphql";

/** A resolved `sportId`, a fallback that settled without one (`none`), or still in flight (`unknown`). */
export type EventViewSportStatus = number | "none" | "unknown";

/**
 * Destination-side warm: the reader primes the cache it is about to read, from the urn it already
 * holds. The write is render-phase deliberately — it must land before the reads below in this same
 * render. Both halves are read and written independently, and only a half that is *both* unwarmed and
 * resolvable is written. Why render-phase is safe: `docs/specs/event-view-apollo-header-migration.md` §13.4.3.
 */
export const useEventViewWarm = (viewURN: string): void => {
  const store = useStore<ApplicationState>();
  const { cache } = useApolloClient();

  // Returned only to satisfy `react-hooks/void-use-memo`; there is no value left to hand back now
  // that the two halves are written independently of each other.
  return useMemo(() => {
    // One probe read for all three halves. Either fixture kind counts as "the fixture is warmed": a
    // view is backed by one of them, so requiring both would rerun the resolver on every mount.
    const { label: labelWarmed, fixture, baseFixture } = readEventViewWarmState(cache, viewURN);
    const fixtureWarmed = fixture || baseFixture;

    if (labelWarmed && fixtureWarmed) return;

    const warm = resolveEventViewWarm(store.getState(), viewURN);

    if (!warm) return;

    // The label writes `EventView.sportevent`; both fixture halves write `EventView.fixture`, so only
    // one may run. Each self-guards on completeness, but the choice is by presence: a football half
    // means a football-backed view, so a refused one never falls back to the base half.
    if (!labelWarmed) writeEventViewLabelWarm(cache, viewURN, warm);
    if (fixtureWarmed) return;
    if (warm.fixture) writeEventViewFixtureWarm(cache, viewURN, warm.fixture);
    else if (warm.baseFixture) writeEventViewBaseFixtureWarm(cache, viewURN, warm.baseFixture);
  }, [cache, store, viewURN]);
};

export const EventViewSportQuery = gql(/* GraphQL */ `
  query EventViewSport($viewURN: URN!) {
    View(viewURN: $viewURN) {
      ... on EventView {
        __typename
        urn
        sportevent {
          urn
          sport {
            urn
            sportId
          }
        }
      }
    }
  }
`);

/**
 * Cache-only first-paint discriminator, primed synchronously by the warm above (which writes the server's
 * own field). Kept separate so a view with no active registry entry never mounts the full EventFixtureView
 * query. `unknown` is strictly in-flight — see the fail-open below.
 */
export const useEventViewSportStatus = (viewURN: string): EventViewSportStatus => {
  useEventViewWarm(viewURN);
  const { data } = useFragment({
    fragment: EventViewLabelWarmFragment,
    fragmentName: "EventViewWarm",
    from: { __typename: "EventView", urn: viewURN },
  });
  const cachedSportId = data?.sportevent?.sport?.sportId;
  const {
    data: queryData,
    loading,
    error,
  } = useQuery(EventViewSportQuery, {
    variables: { viewURN },
    skip: cachedSportId != null,
  });
  const queriedSportId =
    queryData?.View && "__typename" in queryData.View && queryData.View.__typename === "EventView"
      ? queryData.View.sportevent?.sport?.sportId
      : undefined;
  const sportId = cachedSportId ?? queriedSportId;

  if (sportId != null) return sportId;

  // Fail open. A settled fallback that produced no sport (error, null `View`, non-EventView) must not
  // hold `unknown` forever: the callers fail closed on it, so a stuck `unknown` would keep re-deciding
  // the partition on every render instead of latching the legacy path once.
  const settled = !loading && (error != null || queryData !== undefined);

  return settled ? "none" : "unknown";
};

/**
 * A view's sport never changes: a resolved `sportId` is final for the urn, `none` is provisional and a later
 * `sportId` upgrades it, so a partial write dropping `sportevent` can't regress a painted header mid-view.
 * Resets when `viewURN` changes. Spec: `docs/specs/event-view-apollo-all-sports.md` §4.4.
 */
export const useLatchedEventViewSportStatus = (viewURN: string): EventViewSportStatus => {
  const status = useEventViewSportStatus(viewURN);
  const [latched, setLatched] = useState<{ viewURN: string; status: EventViewSportStatus } | null>(null);
  const latchedStatus = latched?.viewURN === viewURN ? latched.status : undefined;

  if (typeof latchedStatus === "number") return latchedStatus;

  if (status !== "unknown" && latchedStatus !== status) setLatched({ viewURN, status });

  return status === "unknown" && latchedStatus !== undefined ? latchedStatus : status;
};

/**
 * Single view-rooted fragment for the Event View (grows per phase). `sportevent` is repeated on
 * siblings so a shallow sibling cache entry still carries the football discriminator — otherwise
 * the header flickers on an in-place swap. Siblings are `EventView`s too, so every nested entity
 * needs its `urn`: without it the write is identity-less and replaces that view's own reference.
 */
export const EventFixtureViewFragment = gql(/* GraphQL */ `
  fragment EventFixtureView on EventView {
    __typename
    urn
    title
    headerTheming
    sportevent {
      urn
      name
      availableToSubscribe
      sport {
        urn
        sportId
      }
    }
    fixture {
      __typename
      urn
      runnerNames {
        home
        away
      }
      ... on BaseFixture {
        ...EventHeaderFixture
      }
      ... on FootballFixture {
        ...FootballFixture
      }
    }
    siblingEventViews {
      urn
      url
      title
      sportevent {
        urn
        name
        sport {
          urn
          sportId
        }
      }
      fixture {
        urn
        runnerNames {
          home
          away
        }
        # Shell: enough for the switch to paint, minus a per-sibling mainMarket resolve nothing renders.
        ... on BaseFixture {
          ...EventHeaderFixtureShell
        }
        ... on FootballFixture {
          ...FootballFixture
        }
      }
    }
  }
`);

export const EventFixtureViewQuery = gql(/* GraphQL */ `
  query EventFixtureView($viewURN: URN!) {
    View(viewURN: $viewURN) {
      ...EventFixtureView
    }
  }
`);

/**
 * Cache-first fetching (tbd-components AGENTS.md): read via `useFragment`, hit the network only when the
 * fragment is incomplete *and* visible; the query writes the cache, re-rendering that read. We render from any
 * cache entry, not just `complete` — shallow sibling writes never are, and gating re-flashed the placeholder.
 */
export const useEventViewQuery = (variables: { viewURN: string }, options: { visible: boolean }) => {
  // Priming only: the warm lands in the same fields this fragment reads, so there is no second
  // value to coalesce — the read below sees it.
  useEventViewWarm(variables.viewURN);
  const { data, complete } = useFragment({
    fragment: EventFixtureViewFragment,
    fragmentName: "EventFixtureView",
    from: { __typename: "EventView", urn: variables.viewURN },
  });

  const skip = !options.visible || complete;
  const { loading, error } = useQuery(EventFixtureViewQuery, {
    variables: { viewURN: variables.viewURN },
    skip,
  });

  // Pull-to-refresh refetch: the `useQuery` above is skipped once complete, so its own `refetch` is
  // unreliable. `network-only` always hits the network. Inert until `refresh()` is invoked.
  const [fetchView] = useLazyQuery(EventFixtureViewQuery, { fetchPolicy: "network-only" });
  const refresh = useCallback(
    () => fetchView({ variables: { viewURN: variables.viewURN } }),
    [fetchView, variables.viewURN],
  );

  // A discriminator-only record must not count. Require a label source rather than a present `title`
  // field: `title` is nullable, and an entry that can only produce `labelFor`'s "" fallback would
  // paint a blank header instead of holding the placeholder. `sportevent.name` covers both the
  // shared-entity label warmed by any home/coupon/list normalize and the resolved warm's own write.
  const hasRenderableCacheEntry = Boolean(
    data?.urn && (data.fixture?.runnerNames || data.sportevent?.name != null || data.title != null),
  );

  // Three consumers key on this — native's latch guard and both fail-open branches — so a warm the
  // query has not answered yet must still read `true`. `!data.fixture` cannot be a term any more (the
  // fixture warm writes one); these two back each other's edge case instead, one per response kind.
  // The warm never writes the bell flag: `docs/specs/event-view-apollo-header-migration.md` §13.4.3 rule 7.
  const warmOnly = hasRenderableCacheEntry && data.title == null && data.sportevent?.availableToSubscribe == null;

  return {
    loading: hasRenderableCacheEntry ? false : skip || loading,
    // Surfaced so a view can fail open when the initial fetch errors with nothing but the warm to
    // show — otherwise the catalogue's own switcher stays excluded behind a dead Apollo one.
    error,
    refresh,
    warmOnly,
    // cast: `data` is a deep-partial; the viewmodel guards each nested field.
    data: { view: hasRenderableCacheEntry ? (data as EventFixtureViewFragmentData) : undefined },
  };
};

const APP_CONTEXT_URN = "ppb:tbd:appContext:appContext";

export const EventViewHeaderThrottleFragment = gql(/* GraphQL */ `
  fragment EventViewHeaderThrottle on AppContextDetails {
    __typename
    throttles {
      name
      isActive
    }
  }
`);

/**
 * Reactive read of every active throttle name, registered or not; `EventView` filters it against the
 * registry. `useFragment`, not one-shot `cache.readFragment`, so `EventView` re-renders when server-driven
 * assignment writes to the cache at boot. `?throttlesOn=` reaches it against a non-production BFF only.
 */
export function useEventViewActiveThrottles(): ReadonlySet<string> {
  const { data } = useFragment({
    fragment: EventViewHeaderThrottleFragment,
    fragmentName: "EventViewHeaderThrottle",
    from: { __typename: "AppContextDetails", urn: APP_CONTEXT_URN },
  });
  const throttles = data?.throttles;

  return useMemo(
    () =>
      new Set((throttles ?? []).flatMap((throttle) => (throttle?.isActive && throttle.name ? [throttle.name] : []))),
    [throttles],
  );
}
